import type { ReactiveController, ReactiveControllerHost } from "lit";

// App-level navigation guard registry.
//
// Any surface holding unsaved work registers a predicate that returns a
// human-readable reason string when leaving would lose data (or a
// falsy value when it's safe to go). The one app router (`app.ts`)
// consults `confirmLeave()` at its navigation chokepoints — link
// clicks, `cos:navigate`, and browser back/forward — plus a single
// app-level `beforeunload` reads `hasBlockers()`. This is the ONLY
// place `window.confirm` is sanctioned for a nav-away guard: it must be
// synchronous to abort a route change (a styled modal can't block one),
// and the codebase's "no window.confirm" preference is delete-only.
//
// Register on connect, unregister on disconnect (the sidePanelManager /
// shortcutRegistry singleton pattern). A predicate is re-evaluated on
// every navigation attempt, so a surface never has to push its dirty
// state — it just answers when asked.

/** Shows the leave question and resolves true to leave, false to stay. */
export type LeavePresenter = (reason: string) => Promise<boolean>;

/** Returns a non-empty reason to block navigation, or a falsy value to allow it. */
export type GuardPredicate = () => string | null | undefined | false;

class NavigationGuard {
	private _guards = new Map<string, GuardPredicate>();

	/** Register (or replace) a surface's guard. Re-registering the same
	 *  key overwrites — safe if a component reconnects. */
	register(key: string, predicate: GuardPredicate): void {
		this._guards.set(key, predicate);
	}

	unregister(key: string): void {
		this._guards.delete(key);
	}

	/** Drop every guard. Used on logout / session expiry, where any surface's
	 *  unsaved work is already unsaveable (the token is gone) — so a
	 *  "you have unsaved changes" prompt must never trap the forced redirect. */
	clear(): void {
		this._guards.clear();
	}

	/** The first registered guard's reason, or null if none block. */
	blockingReason(): string | null {
		for (const predicate of this._guards.values()) {
			let reason: string | null | undefined | false;
			try {
				reason = predicate();
			} catch {
				// A throwing predicate must never wedge navigation — treat
				// it as "no objection" and let the user move.
				reason = null;
			}
			if (reason) return reason;
		}
		return null;
	}

	/** True when any surface would lose work on navigation (drives the
	 *  app-level beforeunload — the browser shows its own generic prompt). */
	hasBlockers(): boolean {
		return this.blockingReason() !== null;
	}

	/** A surface may register a styled dialog for link and menu clicks, where
	 *  the router can wait for an answer. The browser's back/forward still
	 *  uses the synchronous `confirmLeave()`: popstate cannot be paused. */
	private _presenter: LeavePresenter | null = null;

	setPresenter(presenter: LeavePresenter | null): void {
		this._presenter = presenter;
	}

	/** Unregister `presenter` only if it is still the current one. */
	clearPresenter(presenter: LeavePresenter): void {
		if (this._presenter === presenter) this._presenter = null;
	}

	/** The dialog's answer when something blocks AND a presenter is
	 *  registered; null when the caller should fall back to `confirmLeave()`. */
	presentLeave(): Promise<boolean> | null {
		const reason = this.blockingReason();
		if (!reason || !this._presenter) return null;
		return this._presenter(reason);
	}

	/** Synchronous gate for in-app navigation. Returns true to proceed,
	 *  false to abort (the caller must NOT commit the route change). */
	confirmLeave(): boolean {
		const reason = this.blockingReason();
		if (!reason) return true;
		return window.confirm(reason);
	}
}

export const navigationGuard = new NavigationGuard();

let dirtyGuardSeq = 0;

/**
 * One-line adoption of the navigation guard for any editable surface.
 *
 *   private _guard = new DirtyGuardController(this, () => this._dirty);
 *
 * The controller registers a per-instance guard on connect and unregisters
 * on disconnect (Lit drives the lifecycle), so a surface holding unsaved
 * work never has to touch `navigationGuard` directly or hand-roll a
 * `beforeunload`. The per-instance key means a component that reconnects
 * (grid relocation, tab swap) never unregisters a sibling's guard.
 *
 * `isDirty` is re-evaluated on every navigation attempt — return `true`
 * only while there is genuinely unsaved work to lose. `message` is what
 * `window.confirm` shows; keep it a short, honest question.
 */
export class DirtyGuardController implements ReactiveController {
	private readonly _key = `dirty-guard:${++dirtyGuardSeq}`;

	constructor(
		host: ReactiveControllerHost,
		private readonly _isDirty: () => boolean,
		private readonly _message = "You have unsaved changes. Leave without saving?",
	) {
		host.addController(this);
	}

	hostConnected(): void {
		navigationGuard.register(this._key, () => (this._isDirty() ? this._message : null));
	}

	hostDisconnected(): void {
		navigationGuard.unregister(this._key);
	}
}
